import { useState } from 'react'
import { FileIcon } from './FileIcon'
import { recentFiles } from '@/utils/data'
import type { FilterTab, FileType } from '@/types'
import styles from './RecentFiles.module.css'

const TABS: FilterTab[] = ['All', 'Word', 'Excel', 'PowerPoint', 'PDF', 'More']

const TAB_TYPE_MAP: Partial<Record<FilterTab, FileType>> = {
  Word: 'word',
  Excel: 'excel',
  PowerPoint: 'powerpoint',
  PDF: 'pdf',
}

export function RecentFiles() {
  const [activeTab, setActiveTab] = useState<FilterTab>('All')
  const [filterText, setFilterText] = useState('')

  const filtered = recentFiles.filter((file) => {
    const matchesTab =
      activeTab === 'All' || activeTab === 'More' ||
      file.fileType === TAB_TYPE_MAP[activeTab]
    const matchesText =
      file.name.toLowerCase().includes(filterText.toLowerCase()) ||
      file.owner.toLowerCase().includes(filterText.toLowerCase())
    return matchesTab && matchesText
  })

  return (
    <section className={styles.section}>
      <div className={styles.toolbar}>
        <div className={styles.tabs} role="tablist">
          <span className={styles.recentLabel}>Recent</span>
          {TABS.map((tab) => (
            <button
              key={tab}
              role="tab"
              aria-selected={activeTab === tab}
              className={`${styles.tab} ${activeTab === tab ? styles.activeTab : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab !== 'All' && tab !== 'More' && (
                <FileTypeIcon type={TAB_TYPE_MAP[tab]!} />
              )}
              {tab}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Filter by name or person"
          className={styles.filterInput}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          aria-label="Filter files"
        />
      </div>

      <table className={styles.table} role="grid">
        <thead>
          <tr className={styles.headerRow}>
            <th className={styles.th} scope="col">Name</th>
            <th className={styles.th} scope="col">Opened</th>
            <th className={styles.th} scope="col">Owner</th>
            <th className={styles.th} scope="col">Activity</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((file) => (
            <tr key={file.id} className={styles.row} tabIndex={0}>
              <td className={styles.td}>
                <div className={styles.nameCell}>
                  <FileIcon type={file.fileType} size="sm" />
                  <div className={styles.nameText}>
                    <span className={styles.fileName}>{file.name}</span>
                    <span className={styles.fileLocation}>{file.location}</span>
                  </div>
                </div>
              </td>
              <td className={styles.td}>
                <span className={styles.meta}>{file.opened}</span>
              </td>
              <td className={styles.td}>
                <span className={styles.meta}>{file.owner}</span>
              </td>
              <td className={styles.td}>
                <span className={styles.meta}>{file.activity ?? '—'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {filtered.length === 0 && (
        <div className={styles.empty}>No files match your filter.</div>
      )}
    </section>
  )
}

function FileTypeIcon({ type }: { type: FileType }) {
  const icons: Record<FileType, string> = {
    excel: 'https://res-1.public.onecdn.static.microsoft/files/fabric-cdn-prod_20260113.001/assets/brand-icons/product/svg/excel_20x1.svg',
    word: 'https://res-1.public.onecdn.static.microsoft/files/fabric-cdn-prod_20260113.001/assets/brand-icons/product/svg/word_20x1.svg',
    powerpoint: 'https://res-1.public.onecdn.static.microsoft/files/fabric-cdn-prod_20260113.001/assets/brand-icons/product/svg/powerpoint_20x1.svg',
    pdf: 'https://res-1.public.onecdn.static.microsoft/files/fabric-cdn-prod_20260113.001/assets/item-types/20/pdf.svg',
    folder: '#dcb67a',
  }

  const icon = icons[type]

  if (typeof icon === 'string' && icon.startsWith('http')) {
    return (
      <img
        src={icon}
        alt={type}
        style={{
          width: 16,
          height: 16,
          marginRight: 6,
          verticalAlign: 'middle',
          flexShrink: 0,
        }}
      />
    )
  }

  const colors: Record<string, string> = {
    folder: '#dcb67a',
  }

  return (
    <span
      style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        borderRadius: 2,
        backgroundColor: colors[type],
        marginRight: 4,
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    />
  )
}
